import React, { Component } from 'react';
import PropTypes from 'prop-types';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { connect } from 'react-redux';

// constants for equivalence in seconds
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

class MessageStatusIndicator extends Component {
    static propTypes = {
        value: PropTypes.number, // value is expected to be a unix utc timestamp (in seconds)
        text: PropTypes.object.isRequired,
        isMessageRead: PropTypes.bool.isRequired,
        isCurrentUser: PropTypes.bool.isRequired,
    };

    state = {
        timeIndicator: this.getTimeData().text,
        updateTimeout: null,
    };

    handleTimeUpdate() {
        const { text, difference } = this.getTimeData();

        clearTimeout(this.state.updateTimeout);
        this.setState({ timeIndicator: text, updateTimeout: null });

        if (text) {
            let timeout;

            // if the time difference is under an hour,
            // only update in a minute.
            // if difference is less than a day, update in an hour
            // otherwise, only update in a day.

            // obviously these could be much more optimized to be accurate,
            // but it is suspected that putting the page in the background
            // would throw off timers anyway

            if (difference < HOUR) {
                timeout = MINUTE;
            } else if (difference < DAY) {
                timeout = HOUR;
            } else {
                timeout = DAY;
            }

            const updateTimeout = setTimeout(() => {
                this.handleTimeUpdate();
            }, timeout * 1000);

            this.setState({
                updateTimeout,
            });
        }
    }

    getTimeData() {
        const {
            value,
            text: {
                messageRelativeTimeDay,
                messageRelativeTimeHour,
                messageRelativeTimeJustNow,
                messageRelativeTimeMinute,
            },
        } = this.props;

        const received = new Date(value * 1000);
        const difference = differenceInSeconds(new Date(), received);

        let text;

        if (difference < MINUTE) {
            text = messageRelativeTimeJustNow;
        } else if (difference < HOUR) {
            text = messageRelativeTimeMinute.replace('{value}', Math.round(difference / MINUTE).toFixed());
        } else if (difference < DAY) {
            text = messageRelativeTimeHour.replace('{value}', Math.round(difference / HOUR).toFixed());
        } else if (difference <= DAY * 7) {
            text = messageRelativeTimeDay.replace('{value}', Math.round(difference / DAY).toFixed());
        } else if (value) {
            text = '';
        }

        return {
            text,
            difference,
        };
    }

    componentDidMount() {
        this.handleTimeUpdate();
    }

    componentDidUpdate({ value }) {
        if (value !== this.props.value) {
            this.handleTimeUpdate();
        }
    }

    render() {
        const {
            isCurrentUser,
            isMessageRead,
            text: { messageSending, messageDelivered },
        } = this.props;

        let seen;
        let delivered;
        let timeDisplay = messageSending;

        if (this.state.timeIndicator !== undefined) {
            timeDisplay = this.state.timeIndicator;

            if (isCurrentUser) {
                if (!isMessageRead) {
                    timeDisplay += '.';
                    delivered = <b>{messageDelivered}</b>;
                } else {
                    seen = <b>Seen</b>;
                    timeDisplay = timeDisplay.toLowerCase();
                }
            }
        }

        return (
            <div className="message-status-indicator">
                <span className="time">
                    {seen} {timeDisplay} {delivered}
                </span>
            </div>
        );
    }

    componentWillUnmount() {
        if (this.state.updateTimeout) {
            clearTimeout(this.state.updateTimeout);
        }
    }
}

export default connect(
    ({
        ui: {
            text: {
                messageRelativeTimeDay,
                messageRelativeTimeHour,
                messageRelativeTimeJustNow,
                messageRelativeTimeMinute,
                messageSending,
                messageDelivered,
            },
        },
    }) => {
        return {
            text: {
                messageRelativeTimeDay,
                messageRelativeTimeHour,
                messageRelativeTimeJustNow,
                messageRelativeTimeMinute,
                messageSending,
                messageDelivered,
            },
        };
    },
)(MessageStatusIndicator);
